<template>
    <div id="lt-yantr-v2" class="public-service-loan-forgiveness-calculator">
        <div class="main-calc-container main-calc-device-desktop">
            <aside class="main-calc-aside">
                <h4>Personal Info</h4>
                <div class="number-input-error-block">
                    <number-input
                        v-model="AdjustedGrossIncome"
                        :label="labelAdjustedGrossIncome"
                        :class="error.adjustedGrossIncomeError ? 'error-border-red' : ''"
                        inputmode="decimal"
                        max-value="1000000"
                        :maxlength="7"
                        @input="calculationResult(); validateInput();"
                    />
                    <p v-if="error.adjustedGrossIncomeError" class="error-text"> {{ error.adjustedGrossIncomeError }} </p>
                </div>
                <drop-down
                    v-model="FamilySize"
                    label="Family Size"
                    :options="FamilySizeOption"
                    @input="calculationResult();"
                />
                <drop-down
                    v-model="StateOfResidence"
                    label="State of Residence"
                    :options="StateOfResidenceOption"
                    @input="calculationResult();"
                />
                <number-input
                    v-model="AnnualIncomeGrowth"
                    label="Annual Income Growth"
                    inputmode="decimal"
                    symbol="%"
                    symbolAligned="right"
                    :noDecimal="true"
                    precision="2"
                    max-value="10.99"
                    @input="calculationResult(); validateInput();"
                    @inputBlur="calculationResult()"
                />
                <drop-down
                    v-model="AreYouMarried"
                    label="Are you Married?"
                    :options="AreYouMarriedOption"
                    @input="calculationResult();"
                />
                <hr />
                <h4>Loan Info</h4>
                <div class="number-input-error-block">
                    <number-input
                        v-model="TotalFederalStudentLoan"
                        label="Total Federal Student Loan Balance"
                        :class="error.totalFederalStudentLoanError ? 'error-border-red' : ''"
                        inputmode="decimal"
                        max-value="1000000"
                        :maxlength="7"
                        @input="calculationResult(); validateInput();"
                    />
                    <p v-if="error.totalFederalStudentLoanError" class="error-text"> {{ error.totalFederalStudentLoanError }} </p>
                </div>
                <number-input
                    v-model="AverageWeightedRate"
                    label="Avg. Weighted Interest Rate"
                    inputmode="decimal"
                    symbol="%"
                    precision="2"
                    max-value="99.99"
                    :noDecimal="true"
                    symbolAligned="right"
                    @input="calculationResult(); validateInput();"
                />
            </aside>
            <mainCalculation :data="result" />
        </div>
    </div>
</template>
<script>
    import Accounting from 'accounting';
    import mainCalculation from './components/main-calculation.vue';
    import NumberInput from '~/components/number-input.vue';
    import DropDown from '~/components/drop-down.vue';
    import forgivenessHelper from './helpers/public-service-forgiveness-caculator';

    export default {
        props: {
            adjustedGrossIncome: {
                default: 60000,
            },
            labelAdjustedGrossIncome: {
                default: 'Adjusted Gross Income',
            },
            incomeGrowth: {
                default: 3,
            },
            federalLoanBalance: {
                default: 30000,
            },
            avgWeightedRate: {
                default: 5.7,
            },
            familySize: {
                default: 1,
            },
            stateResidence: {
                default: 1,
            },
            married: {
                default: 1,
            },
        },
        data() {
            return {
                result: {},
                AdjustedGrossIncome: Accounting.format(this.adjustedGrossIncome),
                AnnualIncomeGrowth: this.incomeGrowth,
                TotalFederalStudentLoan: Accounting.format(this.federalLoanBalance),
                AverageWeightedRate: this.avgWeightedRate,
                FamilySize: this.familySize,
                StateOfResidence: this.stateResidence,
                AreYouMarried: this.married,
                FamilySizeOption: [
                    {
                        text: '1',
                        value: 1,
                    },
                    {
                        text: '2',
                        value: 2,
                    },
                    {
                        text: '3',
                        value: 3,
                    },
                    {
                        text: '4',
                        value: 4,
                    },
                    {
                        text: '5',
                        value: 5,
                    },
                    {
                        text: '6',
                        value: 6,
                    },
                    {
                        text: '7',
                        value: 7,
                    },
                    {
                        text: '8',
                        value: 8,
                    },
                    {
                        text: '9',
                        value: 9,
                    },
                    {
                        text: '9+',
                        value: 10,
                    },
                ],
                StateOfResidenceOption: [
                    {
                        text: 'Continental U.S.',
                        value: 1,
                    },
                    {
                        text: 'Alaska',
                        value: 2,
                    },
                    {
                        text: 'Hawaii',
                        value: 3,
                    },
                ],
                AreYouMarriedOption: [
                    {
                        text: 'Yes',
                        value: 2,
                    },
                    {
                        text: 'No',
                        value: 1,
                    },
                ],
                error: {
                    adjustedGrossIncomeError: '',
                    totalFederalStudentLoanError: '',
                },
                errorMessages: {
                    adjustedGrossIncome: 'Adjusted Gross Income should be between 1 to 1,000,000',
                    totalFederalStudentLoan: 'Total Federal Student Loan Balance should be between 1 to 1,000,000',
                },
            };
        },
        methods: {
            calculationResult() {
                const married = this.AreYouMarried === 1 ? 'no' : 'yes';
                const data = {
                    disbursed: 10,
                    adjustedGrowthIncome: Accounting.unformat(this.AdjustedGrossIncome),
                    familySize: this.FamilySize,
                    totalDebt: Accounting.unformat(this.TotalFederalStudentLoan),
                    rate: this.AverageWeightedRate,
                    state: parseInt(this.StateOfResidence, 10),
                    married,
                    annualIncomeGrowth: this.AnnualIncomeGrowth,
                    corelationOfIncomeBasedRepayment: 0.15,
                    repayForCorelation: 0.1,
                    results: null,
                    defaultTerm: 120,
                    graduateSchool: false,
                };
                const dataPaye = {
                    disbursed: 10,
                    adjustedGrowthIncome: Accounting.unformat(this.AdjustedGrossIncome),
                    familySize: this.FamilySize,
                    totalDebt: Accounting.unformat(this.TotalFederalStudentLoan),
                    rate: this.AverageWeightedRate,
                    state: parseInt(this.StateOfResidence, 10),
                    annualIncomeGrowth: this.AnnualIncomeGrowth,
                    corelationForPayAsOn: 0.1,
                };
                const calculationData = {
                    standard: forgivenessHelper.getStandardCalculation(data),
                    incomeBasedRepaymentResult: forgivenessHelper.getIncomeBasedRepaymentCalculation(data),
                    repaymentPayAsEarnResult: forgivenessHelper.getRepaymentAsOnCalculation(data),
                    incomeContingentRepaymentResult: forgivenessHelper.getIncomeContingentRepaymentCalculation(data),
                    payAsYouEarnResult: forgivenessHelper.getIncomeBasedRepaymentCalculation(dataPaye),
                };
                this.result = calculationData;
            },
            validateInput() {
                const maxAverageWeightedRate = 99.99;
                const maxTotalFederalStudentLoan = 1000000;
                const maxAnnualIncomeGrowth = 10.99;
                const maxAdjustedGrossIncome = 1000000;
                this.AverageWeightedRate = this.AverageWeightedRate > maxAverageWeightedRate ? maxAverageWeightedRate : this.AverageWeightedRate;
                this.TotalFederalStudentLoan = this.TotalFederalStudentLoan > maxTotalFederalStudentLoan ? maxTotalFederalStudentLoan : this.TotalFederalStudentLoan;
                this.AnnualIncomeGrowth = this.AnnualIncomeGrowth > maxAnnualIncomeGrowth ? maxAnnualIncomeGrowth : this.AnnualIncomeGrowth;
                this.AdjustedGrossIncome = this.AdjustedGrossIncome > maxAdjustedGrossIncome ? maxAdjustedGrossIncome : this.AdjustedGrossIncome;

                if (this.AdjustedGrossIncome < 1) {
                    this.error.adjustedGrossIncomeError = this.errorMessages.adjustedGrossIncome;
                } else {
                    this.error.adjustedGrossIncomeError = '';
                }

                if (this.TotalFederalStudentLoan < 1) {
                    this.error.totalFederalStudentLoanError = this.errorMessages.totalFederalStudentLoan;
                } else {
                    this.error.totalFederalStudentLoanError = '';
                }
            },
        },
        components: {
            NumberInput,
            DropDown,
            mainCalculation,
        },
        mounted() {
            this.validateInput();
            this.calculationResult();
        },
    };
</script>
<style src="../../assets/styles/v2/common.scss" lang="scss"></style>
<style src="./main.scss" lang="scss"></style>
